/**
 * "Which size?" on the customer's bid page (server: services/bushSizes).
 *
 * A planting line the office offered in several sizes shows them as prices under the line — "3 gallon · $45",
 * "15 gallon · $180" — and the total at the bottom follows the tap. One state for the card and the pills
 * (useSizeChoice), held by the page.
 *
 * Every price is the server's: the page sends back which size they tapped and the server says what the bid comes to
 * (POST /quotes/public/:token/choices { sizes }). Accept prices the same picks again. A signed bid shows the size
 * that was agreed and offers no choice.
 */
import React, { useState } from 'react';
import { Leaf } from 'lucide-react';
import { Panel } from './public';

export type SizeRow = { key: string; label: string; price: number; total: number; chosen: boolean };
export type SizeLine = {
  id: number; description: string; quantity: number; optional: boolean; ticked: boolean;
  chosen: string | null; unit_price: number; total: number; sizes: SizeRow[];
};
export type SizeOffer = { on: boolean; locked: boolean; lines: SizeLine[]; words: string };
export type SizePick = { lines: Record<string, string> };

const money = (n: number) => { const v = Math.round(Number(n) * 100) / 100; return `$${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };

export type SizeChoiceState = {
  offer: SizeOffer | null;
  /** The offer's line for a bid line, or null when that line offers no sizes. */
  lineFor: (id: number) => SizeLine | null;
  /** What the line costs with the size they have picked (or the one the bid is on). */
  totalFor: (id: number) => number | null;
  busy: boolean;
  setSize: (id: number, key: string) => void;
  /** What the picks add to (or take off) the bid's own total — the server's number. */
  delta: number;
  picks: Record<string, string>;
};

/** The picks, shared by the card and the pills. `offer` null (nothing offered, or signed) → an idle state. */
export function useSizeChoice(offer: SizeOffer | null, {
  onChange, price,
}: {
  onChange: (pick: SizePick, delta: number) => void;
  /** Ask the server what the picks come to — never worked out here. */
  price: (pick: SizePick) => Promise<{ delta: number; lines: SizeLine[] } | null>;
}): SizeChoiceState {
  const [picks, setPicks] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const l of offer?.lines || []) if (l.chosen) out[String(l.id)] = l.chosen;
    return out;
  });
  const [busy, setBusy] = useState(false);
  const [serverLines, setServerLines] = useState<SizeLine[] | null>(null);
  const [delta, setDelta] = useState(0);
  const lines = serverLines || offer?.lines || [];

  async function apply(next: Record<string, string>) {
    if (!offer) return;
    setPicks(next);
    const pick = { lines: next };
    setBusy(true);
    try {
      const priced = await price(pick);
      if (priced) { setServerLines(priced.lines); setDelta(Number(priced.delta) || 0); onChange(pick, Number(priced.delta) || 0); }
      else onChange(pick, delta);
    } catch { onChange(pick, delta); }
    finally { setBusy(false); }
  }

  const lineFor = (id: number) => lines.find(l => l.id === id) || null;
  return {
    offer, lineFor, busy, delta, picks,
    totalFor: id => { const l = lineFor(id); return l ? l.total : null; },
    setSize: (id, key) => {
      const l = lineFor(id);
      if (!l || !l.sizes.some(s => s.key === key) || picks[String(id)] === key) return;
      apply({ ...picks, [String(id)]: key });
    },
  };
}

/** The sizes under one line, as prices. A signed bid says the size that was agreed instead. */
export function SizeLinePick({ state, id }: { state: SizeChoiceState; id: number }) {
  const l = state.lineFor(id);
  if (!l || !state.offer) return null;
  const picked = state.picks[String(id)] || l.chosen;
  if (state.offer.locked) {
    const size = l.sizes.find(s => s.key === picked) || null;
    return size ? <p className="mt-2 text-[14px] font-semibold text-emerald-800"><Leaf className="inline h-4 w-4 mr-1 -mt-0.5" aria-hidden />{size.label} — {money(size.total)}</p> : null;
  }
  return (
    <div className="mt-2" role="radiogroup" aria-label={`Size for ${l.description}`}>
      <p className="text-[12px] font-semibold uppercase tracking-wide text-gray-600">Pick a size</p>
      <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {l.sizes.map(s => {
          const on = s.key === picked;
          return (
            <button key={s.key} type="button" role="radio" aria-checked={on} disabled={state.busy}
              onClick={() => state.setSize(id, s.key)}
              className={`press min-h-[52px] rounded-2xl border-2 px-2 text-[14px] font-bold ${on ? 'border-emerald-600 bg-emerald-100 text-emerald-900' : 'border-brand-200 bg-white text-gray-700'}`}>
              {s.label}
              <span className="block text-[12px] font-semibold">{money(s.total)}{l.quantity > 1 ? ` · ${l.quantity} × ${money(s.price)}` : ''}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The card above the lines: what the choice is, and what they have picked so far. */
export default function SizeChoice({ offer, state }: { offer: SizeOffer; state: SizeChoiceState }) {
  const lines = state.offer ? state.offer.lines.map(l => state.lineFor(l.id) || l) : offer.lines;
  const chosen = lines
    .map(l => ({ l, size: l.sizes.find(s => s.key === (state.picks[String(l.id)] || l.chosen)) }))
    .filter(x => x.size && x.l.ticked);
  if (offer.locked) {
    if (!chosen.length) return null;
    return (
      <Panel tone="brand">
        <p className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight text-gray-900"><Leaf className="h-5 w-5 text-emerald-600" aria-hidden /> Plant sizes</p>
        <ul className="mt-2 space-y-1">
          {chosen.map(({ l, size }) => (
            <li key={l.id} className="text-[15px] text-gray-900">{l.description} — <b>{size!.label}</b> · {money(size!.total)}</li>
          ))}
        </ul>
      </Panel>
    );
  }
  return (
    <Panel>
      <p className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight text-gray-900"><Leaf className="h-5 w-5 text-emerald-600" aria-hidden /> Choose your plant size</p>
      <p className="text-[15px] text-gray-800 mt-2 leading-relaxed">{offer.words}</p>
      {chosen.length > 0 && (
        <ul className="mt-3 space-y-1">
          {chosen.map(({ l, size }) => (
            <li key={l.id} className="text-[14px] text-gray-700">{l.description}: <b className="text-gray-900">{size!.label}</b> · {money(size!.total)}</li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[13px] text-gray-600">Bigger plants cost more and look finished sooner; smaller ones establish fast and catch up. Either way we plant them the same.</p>
    </Panel>
  );
}
